import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { campaignApi, campaignCanvasserApi, turfMapApi } from '../lib/api'
import type {
  CampaignStatus,
  CampaignDetailDto,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  SaveCampaignScriptConfigRequest,
  RecutPreviewRequest,
  AcceptRecutRequest,
  ManualRecutRequest,
  ForwardAssignmentsRequest,
  PointOfContactDto,
  ImpactSettingsDto,
} from '../types/campaign'
import type { SignalCategory, SignalResolution } from '../types/unionSignals'
import type {
  MintCanvasserRequest,
  RevokeCanvasserGrantRequest,
  CanvasserGrantFieldProfile,
  SetCapRequest,
} from '../types/canvasserGrant'

export function useCampaigns(filters?: { status?: CampaignStatus; search?: string }) {
  return useQuery({
    queryKey: ['campaigns', filters],
    queryFn: async () => {
      const response = await campaignApi.getAll(filters)
      return response.data
    },
  })
}

export function useCampaignDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', id],
    queryFn: async () => {
      if (!id) throw new Error('Campaign ID is required')
      const response = await campaignApi.getById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export const SIGNALS_PAGE_SIZE = 25

/** O2: one page of the union-signal queue for a category. */
export function useCampaignSignals(id: string | undefined, category: SignalCategory, page: number) {
  return useQuery({
    queryKey: ['campaigns', id, 'signals', category, page],
    queryFn: async () => {
      if (!id) throw new Error('Campaign ID is required')
      const response = await campaignApi.signals.list(id, category, page, SIGNALS_PAGE_SIZE)
      return response.data
    },
    enabled: !!id,
  })
}

export function useResolveSignal(id: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ sessionId, action }: { sessionId: string; action: SignalResolution }) => {
      if (!id) throw new Error('Campaign ID is required')
      await campaignApi.signals.resolve(id, sessionId, action)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', id, 'signals'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', id, 'stats'] })
    },
  })
}

export function useCampaignStats(id: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', id, 'stats'],
    queryFn: async () => {
      if (!id) throw new Error('Campaign ID is required')
      const response = await campaignApi.getStats(id)
      return response.data
    },
    enabled: !!id,
    refetchInterval: 30_000,
  })
}

export function useCampaignTurfs(id: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', id, 'turfs'],
    queryFn: async () => {
      if (!id) throw new Error('Campaign ID is required')
      const response = await campaignApi.getTurfs(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCampaignRequest) =>
      campaignApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

export function useUpdateCampaign(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateCampaignRequest) =>
      campaignApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['campaigns', id], data)
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => campaignApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

export function useRefreshCampaignLists(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => campaignApi.refreshLists(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

// =====================================================================
// CAMPAIGN SCRIPT CONFIG HOOKS
// =====================================================================

export function useCampaignScriptConfig(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'scripts'],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID required')
      const res = await campaignApi.getScriptConfig(campaignId)
      return res.data
    },
    enabled: !!campaignId,
  })
}

export function useSaveCampaignScriptConfig(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveCampaignScriptConfigRequest) =>
      campaignApi.saveScriptConfig(campaignId, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['campaigns', campaignId, 'scripts'], data)
      // Invalidate campaign detail so hasScript flag updates immediately
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
    },
  })
}

export function useScriptPicker(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'scripts', 'picker', 'scripts'],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID required')
      const res = await campaignApi.getScriptPicker(campaignId)
      return res.data
    },
    enabled: !!campaignId,
  })
}

export function useTagPicker(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'scripts', 'picker', 'tags'],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID required')
      const res = await campaignApi.getTagPicker(campaignId)
      return res.data
    },
    enabled: !!campaignId,
  })
}

export function useTurfMapPicker(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'scripts', 'picker', 'turfmaps'],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID required')
      const res = await campaignApi.getTurfMapPicker(campaignId)
      return res.data
    },
    enabled: !!campaignId,
  })
}

// =====================================================================
// DAILY REVIEW HOOKS
// =====================================================================

export function useDailyReview(campaignId: string | undefined, date?: string) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'daily-review', date],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID required')
      const res = await campaignApi.getDailyReview(campaignId, date)
      return res.data
    },
    enabled: !!campaignId,
    refetchInterval: 30_000,
  })
}

export function useAvailableCanvassers(campaignId: string | undefined, date: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'available-canvassers', date],
    queryFn: async () => {
      if (!campaignId || !date) throw new Error('Campaign ID and date required')
      const res = await campaignApi.getAvailableCanvassers(campaignId, date)
      // L11 slice 1f: the picker emits Canvasser-grant ids (was User ids); Phase 7 adds portraitUrl.
      return res.data as { canvassers: { grantId: string; fullName: string; portraitUrl?: string | null }[] }
    },
    enabled: !!campaignId && !!date,
  })
}

export function useRecutPreview() {
  // { jobId } — actual completion is polled (WI-41). PreviewRecutFromRemainderAsync never
  // persists anything server-side (all reads are AsNoTracking, no SaveChangesAsync), so there is
  // no cache to invalidate at the poll's terminal-success point either (see
  // TurfReviewMapModal.handleAutoRecut) — the result just loads into local terra-draw state.
  return useMutation({
    mutationFn: ({ turfMapId, request }: { turfMapId: string; request: RecutPreviewRequest }) =>
      turfMapApi.recutPreview(turfMapId, request).then(r => r.data),
  })
}

export function useRecutAccept(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ turfMapId, request }: { turfMapId: string; request: AcceptRecutRequest }) =>
      turfMapApi.recutAccept(turfMapId, request).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['turf-maps'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'daily-review'] })
    },
  })
}

export function useManualRecut(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ turfMapId, request }: { turfMapId: string; request: ManualRecutRequest }) =>
      turfMapApi.manualRecut(turfMapId, request).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['turf-maps'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'daily-review'] })
    },
  })
}

export function useForwardAssignments(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ turfMapId, request }: { turfMapId: string; request: ForwardAssignmentsRequest }) =>
      turfMapApi.forwardAssignments(turfMapId, request).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'daily-review'] })
    },
  })
}

// =====================================================================
// CANVASSER-GRANT HOOKS (L11 slice 1e, Task 13)
// =====================================================================
//
// Query key scheme is hierarchical off the existing ['campaigns', campaignId, ...] root:
//   roster:   ['campaigns', campaignId, 'canvasser-grants']
//   sessions: ['campaigns', campaignId, 'canvasser-grants', grantId, 'sessions']
// Every mutation below invalidates the roster key (turf/session counts and status all live on
// roster rows); revoke-session additionally invalidates the specific sessions-list key since
// that list's `revoked` flag is otherwise invisible to the roster query.

export function useCanvasserRoster(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'canvasser-grants'],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID required')
      const res = await campaignCanvasserApi.roster(campaignId)
      return res.data
    },
    enabled: !!campaignId,
  })
}

export function useMintCanvasser(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: MintCanvasserRequest) =>
      campaignCanvasserApi.mint(campaignId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'canvasser-grants'] })
    },
  })
}

export function useRotateCode(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (grantId: string) =>
      campaignCanvasserApi.rotateCode(campaignId, grantId).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'canvasser-grants'] })
    },
  })
}

export function useRotateLink(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (grantId: string) =>
      campaignCanvasserApi.rotateLink(campaignId, grantId).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'canvasser-grants'] })
    },
  })
}

// NOTE: takes BOTH campaignId and grantId, not just grantId -- the backend route is nested
// under the campaign (GET .../campaigns/{id}/canvasser-grants/{grantId}/sessions), so campaignId
// is required to build the request URL. (The plan's interface list abbreviates this to
// `useGrantSessions(grantId)`; the campaignId param is added here for correctness.)
export function useGrantSessions(campaignId: string | undefined, grantId: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'canvasser-grants', grantId, 'sessions'],
    queryFn: async () => {
      if (!campaignId || !grantId) throw new Error('Campaign ID and grant ID required')
      const res = await campaignCanvasserApi.sessions(campaignId, grantId)
      return res.data
    },
    enabled: !!campaignId && !!grantId,
  })
}

export function useRevokeSession(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ grantId, sessionId }: { grantId: string; sessionId: string }) =>
      campaignCanvasserApi.revokeSession(campaignId, grantId, sessionId).then(r => r.data),
    onSuccess: (_data, { grantId }) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'canvasser-grants'] })
      queryClient.invalidateQueries({
        queryKey: ['campaigns', campaignId, 'canvasser-grants', grantId, 'sessions'],
      })
    },
  })
}

export function useRevokeGrant(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ grantId, body }: { grantId: string; body: RevokeCanvasserGrantRequest }) =>
      campaignCanvasserApi.revokeGrant(campaignId, grantId, body).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'canvasser-grants'] })
    },
  })
}

// [L11-1e][LEGAL] Fix round 1: dedicated hook for the organizer field-profile toggle, wrapping
// campaignCanvasserApi.setFieldProfile's clobber-safe-by-signature call (see lib/api.ts). Callers
// pass the campaign's own already-cached `CampaignDetailDto` (Task 16 reads it via
// useCampaignDetail) plus the new profile -- there is no partial-body path available here.
export function useSetCampaignFieldProfile(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ current, profile }: { current: CampaignDetailDto; profile: CanvasserGrantFieldProfile }) =>
      campaignCanvasserApi.setFieldProfile(campaignId, current, profile).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['campaigns', campaignId], data)
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
    },
  })
}

// =====================================================================
// SHARED CAMPAIGN ENROLLMENT-CODE HOOKS (L11 slice 4, Task 9)
// =====================================================================
//
// Query key: ['campaigns', campaignId, 'enrollment-code']. get() is get-or-create server-side,
// so there is no separate "create" hook. Every mutation below invalidates both the
// enrollment-code key AND the campaign-detail key ['campaigns', campaignId] -- mirrors
// useRotateCode's invalidation shape, extended per this task's brief since campaign detail can
// also reflect the code's on/off state.

export function useEnrollmentCode(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'enrollment-code'],
    queryFn: async () => {
      if (!campaignId) throw new Error('Campaign ID required')
      const res = await campaignApi.enrollmentCode.get(campaignId)
      return res.data
    },
    enabled: !!campaignId,
  })
}

export function useSetCodeActive(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ on }: { on: boolean }) =>
      campaignApi.enrollmentCode.setActive(campaignId, on).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'enrollment-code'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
    },
  })
}

export function useRotateEnrollmentCode(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => campaignApi.enrollmentCode.rotate(campaignId).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'enrollment-code'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
    },
  })
}

export function useSetEnrollmentCap(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ cap }: SetCapRequest) =>
      campaignApi.enrollmentCode.setCap(campaignId, cap).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'enrollment-code'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
    },
  })
}


// =====================================================================
// CAMPAIGN POINT OF CONTACT (canvasser redesign Phase 1)
// =====================================================================

export function usePointOfContact(campaignId: string) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'point-of-contact'],
    queryFn: () => campaignCanvasserApi.getPointOfContact(campaignId).then(r => r.data),
  })
}

export function useSetPointOfContact(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PointOfContactDto) =>
      campaignCanvasserApi.setPointOfContact(campaignId, body).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['campaigns', campaignId, 'point-of-contact'], data)
    },
  })
}

// =====================================================================
// CAMPAIGN IMPACT SETTINGS (canvasser redesign Phase 8)
// =====================================================================

export function useImpactSettings(campaignId: string) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'impact-settings'],
    queryFn: () => campaignCanvasserApi.getImpactSettings(campaignId).then(r => r.data),
  })
}

export function useSetImpactSettings(campaignId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ImpactSettingsDto) =>
      campaignCanvasserApi.setImpactSettings(campaignId, body).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['campaigns', campaignId, 'impact-settings'], data)
    },
  })
}

export function useImpactContests(campaignId: string) {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'impact-contests'],
    queryFn: () => campaignCanvasserApi.getImpactContests(campaignId).then(r => r.data),
    staleTime: 5 * 60_000, // expensive-read endpoint; contests change only with turf/ingest
  })
}
